import {Component, OnInit, inject, signal} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import Swal from "sweetalert2";
import {DataService} from "../../../services/data.service";
import {ProjectStatus, ProjectTab, Right} from "../../interfaces";
import {AccessService} from "../../../services/access.service";
import {ProjectService} from "../../../services/project.service";
import {requiredConfig} from "../required.config";
import {ProjectNamingPipe} from "../../../pipes/project-naming.pipe";
import {ToastrService} from "ngx-toastr";
import {NgIf, DatePipe} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {ProjectIdeaComponent} from './project-idea/project-idea.component';
import {ProjectGoalsComponent} from './project-goals/project-goals.component';
import {ProjectOrganisationComponent} from './project-organisation/project-organisation.component';
import {ProjectCostsComponent} from './project-costs/project-costs.component';
import {ProjectDatesComponent} from './project-dates/project-dates.component';
import {ProjectReportingComponent} from './project-reporting/project-reporting.component';
import {ProjectRisksComponent} from './project-risks/project-risks.component';
import {ProjectStakeholderComponent} from './project-stakeholder/project-stakeholder.component';
import {ProjectExternalComponent} from './project-external/project-external.component';
import {ProjectApprovalsComponent} from './project-approvals/project-approvals.component';
import {ProjectDocumentsComponent} from './project-documents/project-documents.component';
import {ProjectCompletionComponent} from './project-completion/project-completion.component';
import {ProjectStatusPipe} from '../../../pipes/project-status.pipe';

@Component({
    selector: 'app-project-detail',
    templateUrl: './project-detail.component.html',
    styleUrl: './project-detail.component.scss',
    imports: [
        NgIf, RouterLink, TooltipModule, ProjectIdeaComponent, ProjectGoalsComponent, ProjectOrganisationComponent, ProjectCostsComponent, ProjectDatesComponent,
        ProjectReportingComponent, ProjectRisksComponent, ProjectStakeholderComponent, ProjectExternalComponent, ProjectApprovalsComponent, ProjectDocumentsComponent,
        ProjectCompletionComponent, DatePipe, ProjectStatusPipe, ProjectNamingPipe
    ]
})
export class ProjectDetailComponent implements OnInit {

    accessService = inject(AccessService);
    projectService = inject(ProjectService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private dataService = inject(DataService);
    private projectNamingPipe = inject(ProjectNamingPipe);
    private toastrService = inject(ToastrService);

    private readonly requiredConfig = requiredConfig;
    readonly ProjectTab = ProjectTab;
    readonly ProjectStatus = ProjectStatus;
    readonly Right = Right;

    badges = {
        goals: [],
        organisation: [],
        costs: [],
        dates: [],
        completion: [],
        external: [],
    };

    tab: ProjectTab = ProjectTab.IDEA;

    PID: string;
    project: any;

    // Set to true when access to the project is denied as per user settings
    accessDenied = false;

    // Set to true when project does not exist
    missing = false;

    // Disabled when a subcomponent is an edit mode
    navigationDisabled = false;

    // Used to inform children to reload
    reloadCounter = signal(0);

    filedCompletion = false;

    exporting = false;

    ngOnInit(): void {
        this.PID = this.route.snapshot.paramMap.get('id');

        if (this.PID !== '0') {
            this.loadProject();
        }
    }

    // executed when called from children
    handleChangeProject(): void {
        this.loadProject();
    }

    reloadChildren(): void {
        this.reloadCounter.update(v => v + 1);
    }

    loadProject(): void {
        this.dataService.request("Project/get", {
            PID: this.PID
        }).subscribe(response => {
            this.project = response.project;

            this.checkBadges();
        })
    }

    checkBadges(): void {
        this.badges = {
            goals: [],
            organisation: [],
            costs: [],
            dates: [],
            completion: [],
            external: [],
        };

        this.requiredConfig.forEach(config => {
            if (config.required.includes(this.project.status)) {
                if (Array.isArray(this.project[config.field]) && this.project[config.field].length == 0) {
                    this.badges[config.tab].push(config.name);
                } else {
                    if (!this.project[config.field] || this.project[config.field] == '0') {
                        this.badges[config.tab].push(config.name);
                    }
                }
            }
        })
    }

    setTab(tab: ProjectTab): void {
        if (this.navigationDisabled) return;

        this.tab = tab;
    }

    gotoList(): void {
        this.router.navigate(['/project']);
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: this.projectNamingPipe.transform(this.project.status) + ' wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Project/delete', {PID: this.PID}).subscribe(() => {
                    this.router.navigate(['/']);
                });
            }
        });
    }

    toggleNavigation(value: boolean): void {
        this.navigationDisabled = value;
    }

    resetStatus(): void {
        Swal.fire({
            title: 'Sicher?',
            text: "Sicher, dass Sie den Status On Hold aufheben wollen?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Project/resetStatus', {
                    PID: this.PID,
                }).subscribe(() => {
                    this.project.status = this.project.status_prev;

                    this.reloadChildren();
                });
            }
        });
    }

    setStatus(projectStatus: ProjectStatus, text: string): void {
        Swal.fire({
            title: 'Sicher?',
            text: text,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Project/setStatus', {
                    PID: this.PID,
                    status: projectStatus,
                }).subscribe(() => {
                    this.project.status_prev = this.project.status;
                    this.project.status = projectStatus;

                    this.reloadChildren();
                });
            }
        });
    }

    setStatusWithInput(projectStatus: ProjectStatus, text: string): void {
        Swal.fire({
            title: 'Sicher?',
            text: text,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein',
            input: 'text',
            inputLabel: 'Begründung',
            inputValidator: (value) => {
                if (!value) {
                    return 'Bitte eine Begründung eingeben!'
                }
                return null;
            }
        }).then(result => {
            if (result.isConfirmed) {
                this.dataService.request('Project/setStatus', {
                    PID: this.PID,
                    status: projectStatus,
                    input: result.value,
                }).subscribe(() => {
                    this.project.status = projectStatus;

                    this.reloadChildren();
                });
            }
        });
    }

    setStatusWithDoubleInput(projectStatus: ProjectStatus, text: string): void {
        Swal.fire({
            title: 'Sicher?',
            html:
                '<p>' + text + '</p>' +
                '<label for="swal-input1" class="swal2-input-label">Verantwortliche Person</label>' +
                '<input id="swal-input1" class="swal2-input" style="width: calc(100% - 85px)">' +
                '<label for="swal-input2" class="swal2-input-label">Kommentar</label>' +
                '<input id="swal-input2" class="swal2-input" style="width: calc(100% - 85px)">',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein',
            preConfirm: () => {
                // @ts-ignore
                const responsiblePerson = document.getElementById('swal-input1').value;
                // @ts-ignore
                const comment = document.getElementById('swal-input2').value;

                if (!responsiblePerson || !comment) {
                    Swal.showValidationMessage(
                        `Bitte die verantwortliche Person setzen!`
                    )
                    return false;
                } else {
                    return [
                        responsiblePerson,
                        comment
                    ]
                }
            },
        }).then(result => {
            if (result.isConfirmed) {
                this.dataService.request('Project/setStatus', {
                    PID: this.PID,
                    status: projectStatus,
                    input: result.value[0],
                    input2: result.value[1] == '' ? null : result.value[1],
                }).subscribe(() => {
                    this.project.status = projectStatus;

                    this.router.navigate(['/project']);
                });
            }
        });
    }

    /* Mark project as filed for completion so COMPLETION tab shows up, but don't set status yet */
    fileCompletion(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Sicher, dass Sie die den Projektabschluss durchführen wollen?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein',
        }).then(result => {
            if (result.isConfirmed) {
                this.filedCompletion = true;
                this.setTab(this.ProjectTab.COMPLETION);
            }
        });
    }

    onePager() {
        this.exporting = true;
        this.dataService.request('Export/onePager', {
            PID: this.PID,
        }).subscribe(response => {
            this.exporting = false;
            const link = document.createElement('a');

            link.setAttribute('href', 'data:application/pdf;base64,' + response.export.data);
            link.setAttribute('download', response.export.filename);
            link.click();
        });
    }

    refresh() {
        this.loadProject();
        this.reloadChildren();
        this.toastrService.success('Daten wurden neu geladen')
    }
}
